import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getTemplates, uploadTemplate, downloadTemplate, deleteTemplate, TemplateListItem, UseCase, USE_CASE_OPTIONS } from '../services/api'

const glassPanel = { background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(100, 180, 255, 0.1)' }

export default function OGTemplates() {
  const queryClient = useQueryClient()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadProcessing, setUploadProcessing] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<TemplateListItem | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    use_case: 'caf' as UseCase,
  })

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', true],
    queryFn: () => getTemplates(true).then((res) => res.data),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setDeleteConfirm(null)
    },
  })

  // Filter to only show OG templates (category === 'og-template')
  const ogTemplates = templates?.filter((t) => t.category === 'og-template') || []

  const resetForm = () => {
    setFormData({ name: '', use_case: 'caf' })
  }

  const handleUpload = async (file: File) => {
    const isDocx = file.name.endsWith('.docx') || file.name.endsWith('.dotx')
    if (!isDocx) {
      alert('Please upload a Word (.docx or .dotx) file')
      return
    }
    setUploadProcessing(true)
    try {
      const baseName = file.name.replace('.docx', '').replace('.dotx', '')
      const formDataObj = new FormData()
      formDataObj.append('file', file)
      formDataObj.append('name', formData.name || baseName.toLowerCase().replace(/\s+/g, '-'))
      formDataObj.append('display_name', baseName)
      formDataObj.append('description', 'Submitted for AI enhancement')
      formDataObj.append('category', 'og-template')
      formDataObj.append('use_case', formData.use_case)
      await uploadTemplate(formDataObj)
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setShowUploadModal(false)
      resetForm()
    } catch (error: unknown) {
      console.error('Upload failed:', error)
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string }
      const message = axiosError.response?.data?.detail || axiosError.message || 'Unknown error'
      alert(`Failed to upload: ${message}`)
    } finally {
      setUploadProcessing(false)
    }
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0])
  }, [formData])

  const formatTimeAgo = (dateStr: string) => {
    const diffMs = new Date().getTime() - new Date(dateStr).getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Template Inbox</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Submit your dusty old templates for BreezyDoc to breathe new life into</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
          style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24' }}
        >
          <i className="fa-solid fa-upload"></i>
          <span>Submit Template</span>
        </button>
      </div>

      {/* Info Banner */}
      <div className="rounded-xl p-6" style={{ background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <i className="fa-solid fa-wand-magic-sparkles text-xl" style={{ color: '#fbbf24' }}></i>
          </div>
          <div>
            <h2 className="text-lg font-bold mb-1" style={{ color: '#e2e8f0' }}>How it works</h2>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Upload your existing Word templates here. BreezyDoc will analyze them and create AI-powered versions with smart <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' }}>[[prompt]]</code> placeholders that generate content automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Templates List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-3xl mb-4" style={{ color: '#fbbf24' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Loading templates...</p>
          </div>
        </div>
      ) : ogTemplates.length > 0 ? (
        <div className="space-y-3">
          {ogTemplates.map((template) => (
            <div key={template.id} className="rounded-xl p-4 flex items-center justify-between transition hover:scale-[1.01]" style={glassPanel}>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                  <i className="fa-solid fa-file-word text-xl" style={{ color: '#fbbf24' }}></i>
                </div>
                <div>
                  <h3 className="font-semibold" style={{ color: '#e2e8f0' }}>{template.display_name}</h3>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full uppercase" style={{
                      background: template.use_case === 'caf' ? 'rgba(59, 130, 246, 0.15)' :
                                 template.use_case === 'waf' ? 'rgba(52, 211, 153, 0.15)' :
                                 template.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                      color: template.use_case === 'caf' ? '#60a5fa' :
                             template.use_case === 'waf' ? '#34d399' :
                             template.use_case === 'aidw' ? '#fbbf24' : '#94a3b8',
                      border: `1px solid ${template.use_case === 'caf' ? 'rgba(59, 130, 246, 0.3)' :
                                          template.use_case === 'waf' ? 'rgba(52, 211, 153, 0.3)' :
                                          template.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.3)' : 'rgba(148, 163, 184, 0.3)'}`
                    }}>{template.use_case}</span>
                    <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{formatTimeAgo(template.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => downloadTemplate(template.id, `${template.name}.docx`)}
                  className="p-2 rounded-lg transition"
                  style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                  title="Download"
                >
                  <i className="fa-solid fa-download"></i>
                </button>
                <button
                  onClick={() => setDeleteConfirm(template)}
                  className="p-2 rounded-lg transition"
                  style={{ color: 'rgba(248, 113, 113, 0.6)' }}
                  title="Delete"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl p-16 text-center" style={glassPanel}>
          <div className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
            <i className="fa-solid fa-inbox text-4xl" style={{ color: 'rgba(251, 191, 36, 0.4)' }}></i>
          </div>
          <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>No templates submitted yet</h3>
          <p className="mb-6 max-w-md mx-auto" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Got a crusty old Word template gathering dust? Upload it here and let BreezyDoc work its magic!
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-6 py-3 rounded-xl font-medium transition inline-flex items-center space-x-2 hover:scale-105"
            style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24' }}
          >
            <i className="fa-solid fa-upload"></i>
            <span>Submit Your First Template</span>
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && !uploadProcessing && setShowUploadModal(false)}>
          <div className="rounded-2xl max-w-lg w-full" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="px-8 py-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Submit Template</h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Upload a template for AI enhancement</p>
              </div>
              <button onClick={() => !uploadProcessing && setShowUploadModal(false)} disabled={uploadProcessing} style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Template Name (optional)</label>
                  <input type="text" placeholder="Auto-generated from file" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="w-full px-4 py-2.5 rounded-lg text-sm"
                    style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Use Case</label>
                  <select value={formData.use_case} onChange={(e) => setFormData({ ...formData, use_case: e.target.value as UseCase })}
                    className="w-full px-4 py-2.5 rounded-lg text-sm"
                    style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}>
                    {['Frameworks', 'Technology', 'Document Types', 'Services', 'Other'].map((group) => (
                      <optgroup key={group} label={group}>
                        {USE_CASE_OPTIONS.filter((o) => o.group === group).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              {uploadProcessing ? (
                <div className="text-center py-8">
                  <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#fbbf24' }}></i>
                  <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Uploading template...</p>
                </div>
              ) : (
                <div
                  onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                  className="rounded-xl p-8 text-center transition"
                  style={{ border: `2px dashed ${dragActive ? 'rgba(251, 191, 36, 0.5)' : 'rgba(251, 191, 36, 0.3)'}`, background: dragActive ? 'rgba(251, 191, 36, 0.1)' : 'transparent' }}>
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                    <i className="fa-solid fa-file-arrow-up text-3xl" style={{ color: 'rgba(251, 191, 36, 0.5)' }}></i>
                  </div>
                  <p className="mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Drop your template here</p>
                  <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Word documents (.docx, .dotx)</p>
                  <label className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition hover:scale-105 inline-flex items-center space-x-2"
                    style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24' }}>
                    <i className="fa-solid fa-folder-open"></i>
                    <span>Browse Files</span>
                    <input type="file" accept=".docx,.dotx" className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="rounded-2xl max-w-md w-full p-8 text-center" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(248, 113, 113, 0.15)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
              <i className="fa-solid fa-trash text-2xl" style={{ color: '#f87171' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Delete Template?</h3>
            <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Are you sure you want to delete <strong style={{ color: '#e2e8f0' }}>{deleteConfirm.display_name}</strong>? This cannot be undone.
            </p>
            <div className="flex items-center justify-center space-x-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-5 py-2.5 rounded-lg text-sm font-medium transition" style={{ border: '1px solid rgba(100, 180, 255, 0.2)', color: 'rgba(148, 163, 184, 0.8)' }}>
                Cancel
              </button>
              <button onClick={() => deleteMutation.mutate(deleteConfirm.id)} disabled={deleteMutation.isPending}
                className="px-5 py-2.5 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                style={{ background: 'rgba(248, 113, 113, 0.2)', border: '1px solid rgba(248, 113, 113, 0.4)', color: '#f87171' }}>
                {deleteMutation.isPending ? <><i className="fa-solid fa-spinner fa-spin"></i><span>Deleting...</span></> : <><i className="fa-solid fa-trash"></i><span>Delete</span></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
